/**
 * How much one person may ask of the server.
 *
 * On a home network this file would be pointless: the only people who can
 * reach the port are in the same flat. On a public address it is the
 * difference between a server and an invitation, because everything the
 * server does for somebody costs it something and nothing so far said no.
 *
 * Three separate things get away from us, and they need three separate
 * answers rather than one number:
 *
 *   - **Ordinary messages.** A `move` thirty times a second is the design; a
 *     `break` ten thousand times a second is somebody with the console open.
 *     Both look identical to a socket, so what separates them is a rate.
 *
 *   - **`raise`.** A кремль is twenty-one thousand blocks written in one go.
 *     That is the one message where a single frame costs real work, and a
 *     loop sending it is a stopped server rather than a slow one. It gets its
 *     own, much smaller allowance.
 *
 *   - **The door.** The word is two ordinary words because a friend has to
 *     type it off a message - which also means it can be guessed, given
 *     enough tries. Taking the tries away is what makes a short word safe;
 *     making it long enough to be unguessable would make it unusable.
 *
 * Pure and time-injected: nothing here reads a clock, so a test can run an
 * hour of knocking in a millisecond. That is also why the numbers below can
 * be argued about with evidence rather than by feel.
 */

/**
 * A bucket that fills at a steady rate and is spent in gulps.
 *
 * Chosen over "no more than N in the last second" because a player's traffic
 * is genuinely lumpy - a frame drops, three moves arrive together, and a flat
 * per-second cap would punish a bad connection exactly as hard as an attack.
 * A bucket lets the burst through and still holds the average down.
 */
export class Budget {
  private tokens: number;
  private at: number;

  constructor(
    /** Tokens added each second. The sustained rate. */
    private readonly perSecond: number,
    /** The most that can pile up. How lumpy a burst may be. */
    private readonly burst: number,
    now: number,
  ) {
    this.tokens = burst;
    this.at = now;
  }

  /** Spend, if there is enough. False means "over budget" - the caller decides. */
  take(now: number, cost = 1): boolean {
    // Clamped: a clock that steps backwards - a VM resuming, ntp correcting -
    // must not hand out negative time and drain the bucket.
    const elapsed = Math.max(0, now - this.at) / 1000;
    this.tokens = Math.min(this.burst, this.tokens + elapsed * this.perSecond);
    this.at = now;
    if (this.tokens < cost) return false;
    this.tokens -= cost;
    return true;
  }

  /** What is left, for tests and for saying why somebody was dropped. */
  get left(): number {
    return this.tokens;
  }
}

/**
 * Ordinary messages.
 *
 * The client sends its position thirty times a second by design, and a player
 * digging fast adds perhaps ten more at two apiece - call it fifty. Sixty was
 * the first number here and it left ten to spare, which is not a margin, it
 * is a coincidence waiting to be a bug report. A hundred and twenty is twice
 * anything the game can produce and still a fiftieth of what a loop does.
 */
export const MESSAGES_PER_SECOND = 120;
/** Two seconds of lag arriving at once, and no more. */
export const MESSAGE_BURST = 240;

/**
 * How many buildings one player may have waiting to go up.
 *
 * Buildings are queued rather than rationed, and the difference matters.
 * Measured on this world, a хата is two hundred and ten blocks and two
 * milliseconds; a кремль is sixty-two thousand blocks and **two hundred and
 * sixty-four**, which on a one-core server is closer to a second. So they
 * cannot all be done the moment they arrive.
 *
 * But they must not be *refused* either. The client raises the building in
 * its own world first and sends the instruction second - so a refusal leaves
 * one player standing in a fortress nobody else can see, which is the exact
 * failure this whole design is arranged to prevent. A queue drains at one a
 * tick and loses nothing.
 *
 * Six waiting is the point where it stops being somebody building. In
 * creative mode a person can put up several in a row and every one of them
 * arrives; what they cannot do is get six ahead of a queue that empties
 * fifteen times a second, because that takes a script.
 */
export const RAISE_QUEUE_MAX = 6;

/**
 * How far over budget somebody may get before the socket closes.
 *
 * A running score: a dropped message adds one, an accepted one takes one off,
 * and it never goes below nought. Three hundred more dropped than accepted is
 * a flood; a lag spike is a handful that the next quiet second pays back.
 *
 * **Counting them in a row does not work,** which is how this was written
 * first. A steady stream just over the ceiling lets one message through every
 * so often, and a run that resets on success never reaches three hundred - so
 * seven hundred messages in one go left the socket open, and the test that
 * was supposed to catch it passed or failed depending on how busy the machine
 * was. A score that decays cannot be reset by the flood it is counting.
 */
export const PATIENCE = 300;

/** The largest frame we will even look at. A hello is a couple of hundred bytes. */
export const MAX_FRAME = 16 * 1024;

/** How many can be in one world at once. */
export const MAX_PLAYERS = 16;

/** What each kind of message costs against the ordinary budget. */
export function costOf(t: string): number {
  switch (t) {
    // Cheap and constant, and the ones sent every frame.
    case "move": return 1;
    // A block each, but a hundred of them is a wall going up by hand.
    case "place":
    case "break": return 2;
    // Said once. Anything sending these in a loop is not playing.
    case "hello":
    case "ready": return 20;
    default: return 5;
  }
}

/**
 * How many wrong words before the door stops opening.
 *
 * Five, then ten minutes of silence. Against a word picked from two lists
 * that is thousands of years of guessing, and against a friend who typed
 * "калину" instead of "калина" it is four more goes and a cup of tea.
 */
export const WRONG_WORDS_ALLOWED = 5;
export const BARRED_FOR = 10 * 60 * 1000;

/**
 * New connections from one address: twenty at once, then one every six seconds.
 *
 * An address is not a person. A household is behind one, and so is an office;
 * three people each reloading twice while they work out the word is a dozen
 * sockets in a minute from what looks like one caller. Twenty leaves room for
 * that and still stops a script dead, because a script wants thousands.
 */
export const KNOCKS_PER_SECOND = 1 / 6;
export const KNOCK_BURST = 20;

/**
 * An address the door remembers nothing about is dropped after this.
 *
 * Without it the map grows for ever, and "the server falls over after a day
 * of being scanned" is a denial of service written by the thing meant to
 * prevent one.
 */
export const FORGET_AFTER = 60 * 60 * 1000;

interface Caller {
  knocks: Budget;
  /** Wrong words in a row. Cleared the moment one is right. */
  wrong: number;
  /** Barred until this time, or 0. */
  until: number;
  /** Last heard from, so the map can be swept. */
  seen: number;
}

/**
 * The numbers the door runs on. Defaults above; overridden for tests.
 *
 * Made injectable because one branch could not otherwise be reached: a ban
 * is shorter than the sweep, so "do not forget somebody mid-ban" never
 * happens with the real numbers and a test of it was passing for the wrong
 * reason. A guard nobody can make fire is a guard nobody has checked.
 */
export interface DoorPolicy {
  readonly wrongWordsAllowed?: number;
  readonly barredFor?: number;
  readonly knocksPerSecond?: number;
  readonly knockBurst?: number;
  readonly forgetAfter?: number;
}

/**
 * Who has been knocking, and how often.
 *
 * Per address rather than per socket, because a socket is free to open and
 * the whole point is the person who opens ten thousand of them. An address is
 * not a person - a household shares one - which is why the allowances are
 * generous enough for a family and mean enough for a script.
 */
export class Doorman {
  private readonly callers = new Map<string, Caller>();
  private readonly wrongWordsAllowed: number;
  private readonly barredFor: number;
  private readonly knocksPerSecond: number;
  private readonly knockBurst: number;
  private readonly forgetAfter: number;

  constructor(policy: DoorPolicy = {}) {
    this.wrongWordsAllowed = policy.wrongWordsAllowed ?? WRONG_WORDS_ALLOWED;
    this.barredFor = policy.barredFor ?? BARRED_FOR;
    this.knocksPerSecond = policy.knocksPerSecond ?? KNOCKS_PER_SECOND;
    this.knockBurst = policy.knockBurst ?? KNOCK_BURST;
    this.forgetAfter = policy.forgetAfter ?? FORGET_AFTER;
  }

  private caller(from: string, now: number): Caller {
    let caller = this.callers.get(from);
    if (caller === undefined) {
      caller = {
        knocks: new Budget(this.knocksPerSecond, this.knockBurst, now),
        wrong: 0, until: 0, seen: now,
      };
      this.callers.set(from, caller);
    }
    caller.seen = now;
    return caller;
  }

  /** Is this address shut out for guessing? */
  barred(from: string, now: number): boolean {
    const caller = this.callers.get(from);
    if (caller === undefined) return false;
    caller.seen = now;
    if (caller.until === 0) return false;
    if (now >= caller.until) {
      // The sentence is served. Strikes go too, or the next wrong word would
      // bar them again instantly and the ban would be permanent by accident.
      caller.until = 0;
      caller.wrong = 0;
      return false;
    }
    return true;
  }

  /** May this address open another socket? */
  mayKnock(from: string, now: number): boolean {
    if (this.barred(from, now)) return false;
    return this.caller(from, now).knocks.take(now);
  }

  /** They gave the wrong word. Returns true if that was the last straw. */
  wrongWord(from: string, now: number): boolean {
    const caller = this.caller(from, now);
    caller.wrong++;
    if (caller.wrong >= this.wrongWordsAllowed) {
      caller.until = now + this.barredFor;
      return true;
    }
    return false;
  }

  /** They got in. A near miss earlier should not count against them. */
  rightWord(from: string, now: number): void {
    const caller = this.caller(from, now);
    caller.wrong = 0;
    caller.until = 0;
  }

  /** Drop everybody we have not heard from in an hour. Returns how many. */
  forget(now: number): number {
    let dropped = 0;
    for (const [from, caller] of this.callers) {
      // Somebody serving a ban is kept until it expires, or forgetting them
      // would be the way out of it.
      if (caller.until > now) continue;
      if (now - caller.seen < this.forgetAfter) continue;
      this.callers.delete(from);
      dropped++;
    }
    return dropped;
  }

  /** How many addresses are being remembered. For the sweep's own test. */
  get watching(): number {
    return this.callers.size;
  }
}

/**
 * Whose address to count against.
 *
 * Straight off the socket when the server is the thing on the port. Behind a
 * reverse proxy - which is how it gets a certificate, and therefore how it
 * goes on the internet at all - every socket comes from 127.0.0.1 and per
 * address limits would put the whole world in one bucket.
 *
 * `trust` is deliberately a decision made where the server is *deployed*
 * rather than guessed from the request, because a header a client can write
 * is a header a client can lie in: trusting `x-real-ip` on a directly exposed
 * port would let anybody claim a fresh address for every guess and undo the
 * door entirely. The proxy that sets it is the only thing that may be
 * believed, and only the person who put it there knows it is one.
 */
export function clientAddress(
  remote: string | undefined, realIp: string | undefined, trust: boolean,
): string {
  if (trust && realIp !== undefined && realIp.trim() !== "") {
    return realIp.trim();
  }
  return remote ?? "unknown";
}
